<template>
  <!-- A product is framed as what it is. A site capture gets a browser
       window, a portrait app screen gets a phone, and a diagram or a
       photograph gets neither: inventing device chrome around a Petri net
       misdescribes it. -->
  <div class="sf" :class="`sf--${kind}`">
    <div v-if="kind === 'window'" class="sf-bar" aria-hidden="true">
      <span></span><span></span><span></span>
    </div>
    <div v-if="kind === 'phone'" class="sf-screen">
      <img
        class="sf-img"
        :src="url"
        :alt="alt"
        :aria-hidden="alt ? undefined : 'true'"
        :width="w"
        :height="h"
        :loading="eager ? 'eager' : 'lazy'"
        :fetchpriority="eager ? 'high' : undefined"
        decoding="async"
      />
    </div>
    <img
      v-else
      class="sf-img"
      :src="url"
      :alt="alt"
      :aria-hidden="alt ? undefined : 'true'"
      :width="w"
      :height="h"
      :loading="eager ? 'eager' : 'lazy'"
      :fetchpriority="eager ? 'high' : undefined"
      decoding="async"
    />
  </div>
</template>

<script>
import { computed } from "vue";
import { assetUrl } from "@/composables/useProjectAssets";

export default {
  name: "StageFrame",
  props: {
    src: { type: String, required: true },
    kind: { type: String, default: "bare" }, // window | phone | device | bare
    alt: { type: String, default: "" },
    eager: { type: Boolean, default: false },
    /* Intrinsic size, so the box is reserved before the image decodes and
       nothing on the page shifts. */
    w: { type: Number, default: 1700 },
    h: { type: Number, default: 1062 },
  },
  setup(props) {
    return { url: computed(() => assetUrl(props.src)) };
  },
};
</script>

<style scoped>
.sf {
  position: relative;
  width: 100%;
  border-radius: var(--r-lg);
  overflow: hidden;
  background: var(--ground-sunk);
  box-shadow: var(--sh-plate);
}

.sf-img {
  display: block;
  width: 100%;
  height: auto;
}

/* A window, reduced to one hairline bar. Three warm-neutral dots, never the
   red-yellow-green traffic lights, which read as a stock mockup. */
.sf--window .sf-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 12px;
  background: var(--ground-sunk);
  box-shadow: inset 0 -1px 0 var(--hairline);
}
.sf--window .sf-bar span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(22, 19, 15, 0.2);
}
.on-night .sf--window .sf-bar {
  background: #221d19;
  box-shadow: inset 0 -1px 0 rgba(244, 241, 236, 0.12);
}
.on-night .sf--window .sf-bar span {
  background: rgba(244, 241, 236, 0.24);
}

/* A phone, drawn the way the real one is built: a thin titanium rim, a
   thin black bezel, then the screen. No notch is drawn, because every
   screenshot already carries its own status bar and island; a second one
   painted on top was what looked wrong. The body keeps the iPhone's own
   proportions (71.6 x 146.6 mm) so the screen inside lands at its native
   ratio without cropping. */
.sf--phone {
  --rim: 2.5px;
  --bezel: 5px;
  position: relative;
  max-width: 300px;
  margin: 0 auto;
  aspect-ratio: 71.6 / 146.6;
  padding: var(--bezel);
  border-radius: 16.5% / 8.1%;
  background: #0a0908;
  box-shadow:
    0 0 0 var(--rim) #45413c,
    inset 0 0 0 1px rgba(255, 255, 255, 0.05),
    0 8px 18px rgba(20, 17, 14, 0.14),
    0 34px 70px rgba(20, 17, 14, 0.24);
  overflow: visible;
}
.on-night .sf--phone {
  box-shadow:
    0 0 0 var(--rim) #6b665f,
    inset 0 0 0 1px rgba(255, 255, 255, 0.06),
    0 8px 18px rgba(0, 0, 0, 0.3),
    0 34px 70px rgba(0, 0, 0, 0.45);
}
.sf--phone .sf-screen {
  height: 100%;
  width: 100%;
  border-radius: 13.5% / 6.7%;
  overflow: hidden;
  background: #000;
}
.sf--phone .sf-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}
/* The side buttons: volume on the left, power on the right. Two pixels
   proud of the rim is enough for the eye to read a device. */
.sf--phone::before,
.sf--phone::after {
  content: "";
  position: absolute;
  width: 2.5px;
  background: #45413c;
  border-radius: 2px;
}
.sf--phone::before {
  left: calc(-1 * var(--rim) - 2.5px);
  top: 20%;
  height: 22%;
}
.sf--phone::after {
  right: calc(-1 * var(--rim) - 2.5px);
  top: 26%;
  height: 16%;
}
.on-night .sf--phone::before,
.on-night .sf--phone::after {
  background: #6b665f;
}

/* A mockup that already carries its own device: no chrome, no plate, no
   ground. The transparent corners are part of the picture. */
.sf--device {
  background: none;
  box-shadow: none;
  border-radius: 0;
  overflow: visible;
}
.sf--device .sf-img {
  width: auto;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
}

/* Neither: the asset on its own plate, on a ground mixed from its project's
   own colour so a white diagram does not float as a white rectangle. */
.sf--bare {
  background: color-mix(in oklab, var(--p-from) 7%, var(--ground));
  padding: var(--s-4);
  border-radius: var(--r-md);
  box-shadow: var(--sh-2);
}
.on-night .sf--bare {
  background: color-mix(in oklab, var(--p-from) 16%, var(--night));
  box-shadow: var(--sh-night);
}
.sf--bare .sf-img {
  border-radius: var(--r-sm);
}
</style>
